import React, {useEffect, useState} from 'react';
import {Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle} from '@mui/material';
import {getNotificationLog, NotificationLogDetailsResponse} from '../../api/TransactionApi';
import styles from './NotificationDetailsDialog.module.css';

interface NotificationDetailsDialogProps {
    open: boolean;
    notificationId: string | null;
    onClose: () => void;
}

const NotificationDetailsDialog: React.FC<NotificationDetailsDialogProps> = ({open, notificationId, onClose}) => {
    const [notification, setNotification] = useState<NotificationLogDetailsResponse | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            if (!open || !notificationId) {
                if (mounted) {
                    setNotification(null);
                    setError(null);
                    setLoading(false);
                }
                return;
            }
            setLoading(true);
            setError(null);
            try {
                const data = await getNotificationLog(notificationId);
                if (mounted) setNotification(data);
            } catch (e: any) {
                console.error(e);
                if (mounted) {
                    setNotification(null);
                    setError(e?.message ?? 'Failed to load notification');
                }
            } finally {
                if (mounted) setLoading(false);
            }
        };
        load();
        return () => {
            mounted = false;
        };
    }, [open, notificationId]);

    const renderNotificationDetails = (n: NotificationLogDetailsResponse) => {
        const successText = n.success ? 'Yes' : 'No';
        return (
            <div>
                <p>Notification Id: {n.uuid}</p>
                <p>Event Code: {n.eventCode}</p>
                <p>Event Date: {n.eventDate}</p>
                <p>Amount Currency: {n.amountCurrency}</p>
                <p>Amount Value: {n.amountValue}</p>
                <p>Psp Reference: {n.pspReference}</p>
                <p>Transaction Reference: {n.transactionReference}</p>
                <p>Original Reference: {n.originalReference}</p>
                <p>Merchant Reference: {n.merchantReference}</p>
                <p>Merchant Account Code: {n.merchantAccountCode}</p>
                <p>Reason: {n.reason}</p>
                <p>Success: {successText}</p>
                <p>Payment Method: {n.paymentMethod}</p>
                <p>Created At: {n.createdAt}</p>
                <p>Processed At: {n.processedAt ? n.processedAt : 'Not processed'}</p>
                {n.additionalData && (
                    <div>
                        Additional Data
                        <ul>
                            {Object.entries(n.additionalData).map(([key, value]) => (
                                <li key={key}>
                                    {key}: {String(value)}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        );
    };

    let content: React.ReactNode;
    if (loading) {
        content = (
            <div className={styles.div}>
                <CircularProgress/>
            </div>
        );
    } else if (error) {
        content = <div className={styles.divError}></div>;
    } else if (notification) {
        content = renderNotificationDetails(notification);
    } else {
        content = <div>No notification selected</div>;
    }

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Notification Details</DialogTitle>
            <DialogContent>
                {content}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default NotificationDetailsDialog;